import type { ItemAgenda } from '@/pages/agenda/types';
import { contarPorMes, nomeMes } from '@/pages/agenda/utils';

interface VisaoAnoProps {
  ano: number;
  itens: ItemAgenda[];
  onAnterior: () => void;
  onProximo: () => void;
  onAbrirMes: (mes: number) => void;
}

export default function VisaoAno({
  ano,
  itens,
  onAnterior,
  onProximo,
  onAbrirMes,
}: VisaoAnoProps) {
  const contagem = contarPorMes(itens, ano);
  const totalAno = contagem.reduce((soma, c) => soma + c.total, 0);
  const anoAtual = new Date().getFullYear();

  return (
    <div className="mt-2">
      <div className="flex items-center justify-center gap-2">
        <button
          type="button"
          onClick={onAnterior}
          aria-label="Ano anterior"
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-foreground-600 transition-colors hover:bg-background-100 hover:text-foreground-900"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>

        <span className="min-w-[80px] text-center text-base font-bold text-foreground-950">
          {ano}
        </span>

        <button
          type="button"
          onClick={onProximo}
          aria-label="Próximo ano"
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-foreground-600 transition-colors hover:bg-background-100 hover:text-foreground-900"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>

      <p className="mt-2 text-center text-sm text-foreground-600">
        {totalAno === 0
          ? `Nada marcado em ${ano} ainda.`
          : `${totalAno} ${totalAno === 1 ? 'atividade' : 'atividades'} marcadas em ${ano}.`}
      </p>

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        {contagem.map(({ mes, total }) => {
          const ehAtual = ano === anoAtual && mes === new Date().getMonth();
          const temEventos = total > 0;
          return (
            <button
              key={mes}
              type="button"
              onClick={() => onAbrirMes(mes)}
              className={`group cursor-pointer rounded-xl border px-5 py-4 text-left transition-colors ${
                ehAtual
                  ? 'border-primary-300 bg-primary-50 hover:bg-primary-100'
                  : 'border-secondary-200 bg-white hover:border-secondary-300 hover:bg-background-50'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="whitespace-nowrap text-base font-bold capitalize text-foreground-950">
                  {nomeMes(new Date(ano, mes, 1))}
                </span>
                <span
                  className={`flex h-7 min-w-7 items-center justify-center rounded-full px-2 text-xs font-semibold tabular-nums ${
                    temEventos
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-500'
                  }`}
                >
                  {total}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-1.5 text-sm text-foreground-600">
                {temEventos ? (
                  <>
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-calendar-check-line text-primary-500" />
                    </span>
                    <span>
                      {total} {total === 1 ? 'atividade' : 'atividades'}
                    </span>
                  </>
                ) : (
                  <span className="text-foreground-400">Nada marcado</span>
                )}
                <span className="ml-auto flex h-4 w-4 items-center justify-center text-foreground-400 transition-colors group-hover:text-primary-500">
                  <i className="ri-arrow-right-s-line" />
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}